import type { ReactNode } from 'react';
import { styles } from './styles';
import type { DetailSection, DetailTab } from './types';
import { TabbedSections } from './tabbed-sections';

/**
 * The body of a ready surface. If tabs are supplied (and `tabsInHeader` is
 * false) it renders a tab strip + panels; otherwise sections render as a
 * responsive grid of cards. When the tab strip lives inside the accent
 * header card, only the panel for the currently-active tab is rendered.
 */
export function DetailBody({
  sections,
  tabs,
  tabsInHeader,
  activeTabId,
}: {
  sections: ReadonlyArray<DetailSection>;
  tabs?: ReadonlyArray<DetailTab>;
  tabsInHeader?: boolean;
  activeTabId?: string;
}): ReactNode {
  if (tabs && tabs.length > 0 && !tabsInHeader) {
    return <TabbedSections sections={sections} tabs={tabs} />;
  }
  if (tabs && tabs.length > 0 && tabsInHeader && activeTabId) {
    return <ActivePanelOnly sections={sections} activeTabId={activeTabId} />;
  }
  return <SectionGrid sections={sections} />;
}

/** Inline card grid for the no-tabs case. */
function SectionGrid({ sections }: { sections: ReadonlyArray<DetailSection> }): ReactNode {
  return (
    <div className={styles.sectionGrid}>
      {sections.map((section) => (
        <SectionCard key={section.id} section={section} />
      ))}
    </div>
  );
}

/** One section, rendered inline as a card. */
function SectionCard({ section }: { section: DetailSection }): ReactNode {
  return (
    <article className={styles.section} aria-labelledby={`${section.id}-heading`}>
      <h2 id={`${section.id}-heading`} className={styles.sectionHeading}>
        {section.label}
      </h2>
      {section.content}
    </article>
  );
}

/** One tabbed panel. Hidden panels stay mounted so form state survives a tab switch. */
function TabPanel({ section, selected }: { section: DetailSection; selected: boolean }): ReactNode {
  return (
    <div
      role="tabpanel"
      id={`${section.id}-panel`}
      aria-labelledby={`${section.id}-tab`}
      className={styles.panel}
      hidden={!selected}
    >
      <h2 className={styles.sectionHeading}>{section.label}</h2>
      {section.content}
    </div>
  );
}

/**
 * Body variant used when the tab strip lives inside the accent header card.
 * Renders only the panel for the currently-active tab. Inline sections are
 * shown above the panel, like the body-tabbed layout.
 */
function ActivePanelOnly({
  sections,
  activeTabId,
}: {
  sections: ReadonlyArray<DetailSection>;
  activeTabId: string;
}): ReactNode {
  return (
    <div className={styles.tabbed}>
      {sections
        .filter((section) => section.inline)
        .map((section) => (
          <SectionCard key={section.id} section={section} />
        ))}
      {sections
        .filter((section) => !section.inline)
        .map((section) => (
          <TabPanel key={section.id} section={section} selected={section.id === activeTabId} />
        ))}
    </div>
  );
}